import React from 'react';
import { t } from '../../utils/translations';
import { getFormattedUserName } from '../../utils/cvUtils';

export default function CVTemplateATSClassic({ user, projects, language = 'id' }) {
  const fallbackAvatar = 'https://ui-avatars.com/api/?name=User&background=000000&color=fff';

  // Helper function to categorize education
  const categorizeEducation = (education) => {
    const higher = [];
    const k12 = [];
    
    education.forEach(edu => {
      const degree = edu.degree?.toLowerCase() || '';
      const isHigherEd = degree.includes('sarjana') || degree.includes('s1') || 
                        degree.includes('s2') || degree.includes('s3') || 
                        degree.includes('bachelor') || degree.includes('master') || 
                        degree.includes('magister') || degree.includes('phd') || 
                        degree.includes('doktor') || degree.includes('diploma');
      
      if (isHigherEd) {
        higher.push(edu);
      } else {
        k12.push(edu);
      }
    });
    
    return { higher, k12 };
  };

  const { higher: higherEducation, k12: k12Education } = user?.education ? 
    categorizeEducation(user.education) : { higher: [], k12: [] };

  return (
    <div className="bg-white p-8 max-w-[21cm] mx-auto" style={{ fontFamily: 'Times New Roman, serif' }}>
      {/* Header with Photo on Top */}
      <div className="text-center mb-6">
        <div className="w-36 h-36 mx-auto mb-4 border-2 border-gray-400 overflow-hidden bg-gray-100">
          {user?.photo ? (
            <img src={user.photo} alt={user.full_name} className="w-full h-full object-cover" />
          ) : (
            <img src={fallbackAvatar} alt="Avatar" className="w-full h-full object-cover" />
          )}
        </div>
        <h1 className="text-2xl font-bold text-black mb-1">{getFormattedUserName(user) || 'YOUR NAME'}</h1>
        <p className="text-base text-black mb-3 text-center">{user?.job || 'Professional Title'}</p>
        <div className="text-sm text-black space-y-1">
          {user?.email && <div>{user.email}</div>}
          {user?.phone && <div>{user.phone}</div>}
          {user?.location && <div>{user.location}</div>}
        </div>
      </div>

      <hr className="border-t-2 border-black mb-5" />

      {/* Objective/Summary */}
      {user?.summary && (
        <div className="mb-5">
          <h2 className="text-base font-bold text-black mb-2">
            {t('careerObjective', language)}
          </h2>
          <p className="text-sm text-black leading-relaxed text-justify">{user.summary}</p>
        </div>
      )}

      {/* Education - Higher Education First (Traditional ATS Order) */}
      {higherEducation.length > 0 && (
        <div className="mb-5">
          <h2 className="text-base font-bold text-black mb-2">
            {t('higherEducation', language)}
          </h2>
          <div className="space-y-3">
            {higherEducation.map((edu, i) => (
              <div key={i}>
                <div className="flex justify-between">
                  <h3 className="text-sm font-bold text-black">{edu.degree}</h3>
                  <span className="text-sm text-black">{edu.year}</span>
                </div>
                <p className="text-sm text-black italic">{edu.school || edu.institution}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Education - K-12 */}
      {k12Education.length > 0 && (
        <div className="mb-5">
          <h2 className="text-base font-bold text-black mb-2">
            {t('formalEducation', language)}
          </h2>
          <div className="space-y-3">
            {k12Education.map((edu, i) => (
              <div key={i}>
                <div className="flex justify-between">
                  <h3 className="text-sm font-bold text-black">{edu.degree}</h3>
                  <span className="text-sm text-black">{edu.year}</span>
                </div>
                <p className="text-sm text-black italic">{edu.school || edu.institution}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Skills */}
      {user?.skills && user.skills.length > 0 && (
        <div className="mb-5">
          <h2 className="text-base font-bold text-black mb-2">
            {t('skills', language)}
          </h2>
          <div className="text-sm text-black">
            {user.skills.map((skill, i) => (
              <span key={i}>
                {skill}{i < user.skills.length - 1 ? ', ' : ''}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Professional Experience */}
      {user?.experience && user.experience.length > 0 && (
        <div className="mb-5">
          <h2 className="text-base font-bold text-black mb-2">
            {t('professionalExperience', language)}
          </h2>
          <div className="space-y-4">
            {user.experience.map((exp, i) => (
              <div key={i}>
                <div className="flex justify-between items-baseline">
                  <h3 className="text-sm font-bold text-black">{exp.position}</h3>
                  <span className="text-sm text-black">{exp.period}</span>
                </div>
                <p className="text-sm text-black italic mb-1">{exp.company}</p>
                {exp.description && (
                  <p className="text-sm text-black text-justify">{exp.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Projects */}
      {projects && projects.length > 0 && (
        <div className="mb-5">
          <h2 className="text-base font-bold text-black mb-2">
            {t('projects', language)}
          </h2>
          <div className="space-y-2">
            {projects.map((proj, i) => (
              <div key={i}>
                <h3 className="text-sm font-bold text-black">{proj.title}</h3>
                <p className="text-sm text-black text-justify">{proj.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
